// Requests for the foresight steps. Paths come from API_ENDPOINTS (config/api.js).
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import useContentRefresh from '../hooks/useContentRefresh';

const storageKey = (instanceId) => `kuutio-foresight-scenario:${instanceId}`;

function stored(instanceId) {
  try { return window.localStorage.getItem(storageKey(instanceId)); } catch { return null; }
}

function remember(instanceId, scenarioId) {
  try {
    if (scenarioId) window.localStorage.setItem(storageKey(instanceId), scenarioId);
    else window.localStorage.removeItem(storageKey(instanceId));
  } catch { /* private mode: the URL still carries it */ }
}

/**
 * The foresight scenario the steps work on: ?skenaario= in the URL (the
 * participant menu keeps the query string). Without it a step shows its
 * scenario list, which highlights the one this device last chose for the
 * instance. Returns [scenarioId | null, select(id | null), lastId | null];
 * select(null) goes back to the list and keeps the remembered one.
 */
export function useForesightRun(instanceId) {
  const [params, setParams] = useSearchParams();
  const scenarioId = params.get('skenaario') || null;
  const lastId = scenarioId || (instanceId ? stored(instanceId) : null) || null;

  useEffect(() => {
    if (instanceId && scenarioId) remember(instanceId, scenarioId);
  }, [instanceId, scenarioId]);

  const select = useCallback((id) => {
    if (id) remember(instanceId, id);
    const next = new URLSearchParams(params);
    if (id) next.set('skenaario', id);
    else next.delete('skenaario');
    setParams(next);
  }, [instanceId, params, setParams]);

  return [scenarioId, select, lastId];
}

// Called after every successful write on this device. The page's scenario
// list (with each scenario's progress) reloads on it: a step's approval is
// written by the step, and useContentRefresh only hears other devices once
// the shared socket exists.
const writeListeners = new Set();

/** Subscribe to this device's foresight writes; returns the unsubscribe. */
export function onForesightWrite(fn) {
  writeListeners.add(fn);
  return () => writeListeners.delete(fn);
}

/** JSON request with the session cookie; throws the server's error message. */
export async function request(url, { method = 'GET', body } = {}) {
  const res = await fetch(url, {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    // Foresight routes send { error: string, code }; requireInstanceAccess sends { error: { code, message } }.
    const message = typeof data.error === 'string' ? data.error : data.error?.message;
    const err = new Error(message || `HTTP ${res.status}`);
    err.code = data.code || data.error?.code;
    throw err;
  }
  if (method !== 'GET') writeListeners.forEach((fn) => fn());
  return data;
}

/**
 * Loads `url` and reloads it when another device changes the foresight
 * content (instance-content-changed, resource 'foresight').
 */
export function useForesightResource(instanceId, url) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const load = useCallback(async () => {
    if (!url) return;
    try {
      setData(await request(url));
      setError(null);
    } catch (e) {
      setError(e);
    }
  }, [url]);
  useEffect(() => { load(); }, [load]);
  useContentRefresh(instanceId, ['foresight'], load, { minIntervalMs: 1500, enabled: Boolean(instanceId) });
  return { data, error, reload: load };
}
